import { type ComponentType, Suspense, use } from 'react';

import { ComponentErrorBoundary } from '~/components/ComponentErrorBoundary';

export const LAZY_BLOCKS_ELEMENT_ID = 'lazy-blocks';

const serverBlockLoads: Promise<void>[] = [];
const clientBlockLoads = new Map<string, () => Promise<void>>();
const renderedBlocks = new Set<string>();

// Publisher HTML can contain an element with the same id, so anything unreadable means no blocks.
const blocksOnPage = (): string[] => {
  try {
    const names: unknown = JSON.parse(document.getElementById(LAZY_BLOCKS_ELEMENT_ID)?.textContent || '[]');
    return Array.isArray(names) ? names.map(String) : [];
  } catch {
    return [];
  }
};

// The server imports each block up front, so SSR output is unchanged. The client loads the chunks of the blocks the
// server rendered before hydrating, because React drops server HTML that is still suspended when its parent updates.
export const lazyBlock = <Props extends object>(name: string, load: () => Promise<ComponentType<Props>>) => {
  let LoadedBlock: ComponentType<Props> | undefined;
  let loading: Promise<void> | undefined;
  const loadBlock = () => {
    loading ??= load().then((component) => {
      LoadedBlock = component;
    });
    return loading;
  };
  if (import.meta.env.SSR) {
    // A block that fails to import renders through the client fallback instead of failing every page.
    serverBlockLoads.push(loadBlock().catch((error) => console.error(`Failed to load the ${name} block`, error)));
  } else {
    clientBlockLoads.set(name, loadBlock);
    if (blocksOnPage().includes(name)) {
      loadBlock().catch(() => undefined);
    }
  }
  // One component type for the block's whole life, so a chunk landing after the first render does not remount it.
  const Block = (props: Props) => {
    if (!LoadedBlock) {
      use(loadBlock());
    }
    return LoadedBlock ? <LoadedBlock {...props} /> : null;
  };

  return (props: Props) => {
    if (import.meta.env.SSR) {
      renderedBlocks.add(name);
    }
    return (
      <ComponentErrorBoundary fallbackRender={() => null}>
        <Suspense fallback={null}>
          <Block {...props} />
        </Suspense>
      </ComponentErrorBoundary>
    );
  };
};

// Awaited by entry.server before rendering. A top-level await here left module evaluation unsettled.
export const serverBlocksReady = () => Promise.all(serverBlockLoads);

// renderToString is synchronous, so the set holds exactly the blocks of the render that just finished.
export const takeRenderedBlocks = () => {
  const names = [...renderedBlocks];
  renderedBlocks.clear();
  return names;
};

export const injectRenderedBlocks = (html: string, names: string[]) =>
  names.length
    ? html.replace(`<script type="application/json" id="${LAZY_BLOCKS_ELEMENT_ID}"></script>`, (tag) =>
        tag.replace('></script>', `>${JSON.stringify(names)}</script>`),
      )
    : html;

// A chunk that fails to load must not block hydration or a navigation; its block then renders nothing.
const loadBlocks = (names: Iterable<string>) =>
  Promise.all(
    [...names].map((name) =>
      clientBlockLoads
        .get(name)?.()
        .catch(() => undefined),
    ),
  );

// Past a cap the wait gives up and the block mounts when its chunk arrives, so a stalled chunk never holds the page.
const HYDRATION_BLOCK_WAIT_MS = 10_000;
const NAVIGATION_BLOCK_WAIT_MS = 3000;

const capped = (loads: Promise<unknown>, waitMs: number) =>
  Promise.race([loads, new Promise((resolve) => setTimeout(resolve, waitMs))]);

export const clientBlocksReady = () => capped(loadBlocks(blocksOnPage()), HYDRATION_BLOCK_WAIT_MS);

const nodeTypesIn = (value: unknown, types = new Set<string>()) => {
  if (Array.isArray(value)) {
    for (const item of value) {
      nodeTypesIn(item, types);
    }
  } else if (value && typeof value === 'object') {
    for (const [key, child] of Object.entries(value)) {
      if (key === 'type' && typeof child === 'string') {
        types.add(child);
      } else {
        nodeTypesIn(child, types);
      }
    }
  }
  return types;
};

// A client navigation waits for the blocks in its loader data, so they arrive with the page instead of shifting it.
export const withLazyBlocks = <Args, Data>(clientLoader: (args: Args) => Promise<Data>) => {
  const loadPageWithBlocks: ((args: Args) => Promise<Data>) & { hydrate?: boolean } = async (args) => {
    const data = await clientLoader(args);
    await capped(loadBlocks(nodeTypesIn(data)), NAVIGATION_BLOCK_WAIT_MS);
    return data;
  };
  return loadPageWithBlocks;
};
